'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { AdTags } from '@/app_lib/services/ads/defaults';
import { useUser } from '@/app_lib/providers/UserProvider';
import { AnalyticsEvent } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

export interface StnVideoProps {
  dataCy?: string;
  videoId: string;
  adTags?: string[];
  floatVerticalTranslate?: number;
  float?: boolean;
  className?: string;
  'aria-label'?: string;
  target?: string;
  dedupKey?: string;
}

export const StnVideo = ({
  videoId,
  adTags,
  dataCy = '',
  floatVerticalTranslate = 0,
  float = false,
  className,
  target: t = 's2nPlayer',
  'aria-label': ariaLabel = 'Featured Video',
  dedupKey,
}: StnVideoProps) => {
  const { personalizedAds } = useUser();
  const [apiReady, setApiReady] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const updateStnCid = Boolean(adTags?.find((tag) => tag === AdTags.NoVideoAds || tag === AdTags.NoAds));
  const updatedVideoId = updateStnCid ? videoId.replace(/-14497$/g, '-16481') : videoId;

  const target = `${t}-${updatedVideoId}`;
  const { userFlags: userVariants } = useOptimizely();
  const disableDocking = (userVariants?.stnVideo_dockedpw as { variant: string })?.variant === 'on';

  useEffect(() => {
    window.setupS2NApi = (getS2nApiForPlayer: (player: Element) => void) => {
      const s2nPlayer = document.querySelector(`.${target}`);
      if (s2nPlayer) {
        window.s2nApi = getS2nApiForPlayer(s2nPlayer) as unknown as { float: boolean; floatVerticalTranslate: number };
        setApiReady(true);
      }
    };

    return () => {
      delete window.setupS2NApi;
    };
  }, [target]);

  useEffect(() => {
    const pianoModalObserver = new MutationObserver((records) => {
      const modalWasAdded = records.some((mutation) => {
        const currentRecordAddedModal = Array.from(mutation.addedNodes).some((added) => {
          return (added as HTMLElement)?.classList?.contains('tp-modal');
        });

        return currentRecordAddedModal;
      });

      if (modalWasAdded) {
        setModalVisible(true);
        return;
      }

      const modalWasRemoved = records.some((mutation) => {
        const currentRecordRemovedModal = Array.from(mutation.removedNodes).some((removed) => {
          return (removed as HTMLElement)?.classList?.contains('tp-modal');
        });

        return currentRecordRemovedModal;
      });

      if (modalWasRemoved) {
        setModalVisible(false);
        return;
      }
    });

    pianoModalObserver.observe(document.body, { childList: true });

    return () => pianoModalObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!apiReady) {
      return;
    }

    window.s2nApi.floatVerticalTranslate = floatVerticalTranslate;
    if (disableDocking) {
      window.s2nApi.float = false;
    } else {
      window.s2nApi.float = float && !modalVisible;
    }
  }, [apiReady, float, floatVerticalTranslate, modalVisible, disableDocking]);

  if (personalizedAds !== true) {
    return null;
  }

  return (
    <>
      <Script
        aria-label={ariaLabel}
        key={target}
        src={`https://embed.sendtonews.com/player3/embedcode.js?SC=${updatedVideoId}${dedupKey ? `&_=${dedupKey}` : ''}`}
        strategy='afterInteractive'
      />
      <div
        className={cn(`stn-video-player k-${updatedVideoId}`, target, className, AnalyticsEvent.ImpressionElement)}
        data-content-placement='stn-video'
        data-cy={dataCy}
        data-s2n-api='setupS2NApi'
        data-type='float'
        key={`stn-video-player-${target}`}
      />
    </>
  );
};
